"use client";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent } from "../../../components/ui/Card";
import { FaCheckCircle, FaClock, FaHeart, FaSearch } from "react-icons/fa";
import axios from "axios";

export default function CustomerDashboard() {
  const [stats, setStats] = useState({
    completed: 0,
    pending: 0,
    wishlist: 0,
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // New state for tracking order
  const [awb, setAwb] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);

  // ✅ Get token & userId from localStorage
  const authToken = localStorage.getItem("authToken");
  const authUserId = localStorage.getItem("authUserId");
  const navigate = useNavigate();

  // Fetch dashboard data
  useEffect(() => {
    async function fetchDashboard() {
      try {
        setLoading(true);

        if (!authToken || !authUserId) {
          console.error("Auth token or userId not found in localStorage");
          setLoading(false);
          navigate("/login"); // ✅ Redirect to login
          return;
        }

        const res = await axios.get(
          `https://tyka.premierhostings.com/beta/api/user-dashboard?user_id=${authUserId}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        console.log("Dashboard API Response:", res.data);

        if (res.data && res.data.stats) {
          setStats({
            completed: res.data.stats.completed_orders || 0,
            pending: res.data.stats.pending_orders || 0,
            wishlist: res.data.stats.wishlist || 0,
          });
        }

        if (res.data && res.data.orders && res.data.orders.data) {
          setOrders(res.data.orders.data);
        }
      } catch (err) {
        console.error("Error fetching dashboard data", err);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboard();
  }, [authToken, authUserId]);

  // Track Order function
  const trackOrder = async () => {
    if (!awb) {
      alert("Please enter an AWB number");
      return;
    }

    setTrackingLoading(true);
    setTrackingError(null);

    try {
      const res = await axios.get(
        `https://tyka.premierhostings.com/beta/api/shipments/track?awb=${awb}`
      );

      if (res.data && res.data.awb) {
        setTrackingData(res.data);
      } else {
        setTrackingError("No tracking data found for the given AWB.");
      }
    } catch (error) {
      setTrackingError("Error tracking the order. Please try again later.");
    } finally {
      setTrackingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-lg text-gray-500">
        Loading your dashboard...
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 bg-gray-50 dark:bg-transparent min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-red-600">
        My Dashboard
      </h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Completed Orders */}
        <Card className="bg-white  shadow-md rounded-2xl p-4 hover:shadow-lg transition">
          <CardContent className="flex items-center gap-4">
            <div className="p-4 rounded-full bg-green-100 dark:bg-green-800">
              <FaCheckCircle className="text-green-600 text-3xl" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">
                Completed Orders
              </p>
              <h2 className="text-2xl font-bold text-gray-900 ">
                {stats.completed}
              </h2>
            </div>
          </CardContent>
        </Card>

        {/* Pending Orders */}
        <Card className="bg-white  shadow-md rounded-2xl p-4 hover:shadow-lg transition">
          <CardContent className="flex items-center gap-4">
            <div className="p-4 rounded-full bg-yellow-100 dark:bg-yellow-800">
              <FaClock className="text-yellow-600 text-3xl" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">
                Pending Orders
              </p>
              <h2 className="text-2xl font-bold text-gray-900 ">
                {stats.pending}
              </h2>
            </div>
          </CardContent>
        </Card>

        {/* Wishlist */}
        <Card className="bg-white  shadow-md rounded-2xl p-4 hover:shadow-lg transition">
          <CardContent className="flex items-center gap-4">
            <div className="p-4 rounded-full bg-pink-100 dark:bg-pink-800">
              <FaHeart className="text-pink-600 text-3xl" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">
                Wishlist
              </p>
              <h2 className="text-2xl font-bold text-gray-900 ">
                {stats.wishlist}
              </h2>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
